import {
  TreeSnapshot,
  ProjectTreeData,
  PulseTreeData,
  TaskCounts,
  TaskDetail,
} from '@shared/projectTreeDto';

/**
 * WebSocket Event Interface
 */
export interface WebSocketEvent {
  type: string; // 'pulse.created', 'task.updated', etc.
  data: any; // Event payload
}

/**
 * Map task status to taskCounts field
 */
function getCountFieldForStatus(status: string): 'done' | 'working' | 'other' {
  if (status === 'done') return 'done';
  if (status === 'working_on_it') return 'working';
  return 'other';
}

/**
 * Immutably update a project within the snapshot
 */
export function updateProjectInSnapshot(
  snapshot: TreeSnapshot,
  projectUpdate: Partial<ProjectTreeData>
): TreeSnapshot {
  const { id } = projectUpdate;
  if (!id) return snapshot;

  return {
    ...snapshot,
    years: snapshot.years.map(year => ({
      ...year,
      projects: year.projects.map(project =>
        project.id === id
          ? { ...project, ...projectUpdate }
          : project
      )
    })),
    projectMap: snapshot.projectMap
      ? {
          ...snapshot.projectMap,
          [id]: snapshot.projectMap[id]
            ? { ...snapshot.projectMap[id], ...projectUpdate }
            : projectUpdate as ProjectTreeData
        }
      : undefined
  };
}

/**
 * Fields shared between the rich pulse rows and the lightweight
 * `project.pulseTimings` projection consumed by the timeline overlay.
 * A pulse update carrying any of these must also patch the timing entry,
 * otherwise the timeline (which prefers `pulseTimings`) stays stale —
 * e.g. the preview-link icon's `previewShareActive` flag (task #165).
 */
const PULSE_TIMING_FIELDS = [
  'dateAdded',
  'dueDate',
  'category',
  'milestoneColor',
  'previewShareActive',
  'finalShareActive',
  'linkedSharePulseId',
  'linkedShareType',
  'linkedShareActive',
] as const;

/**
 * Find and immutably update a pulse within the snapshot
 */
export function updatePulseInSnapshot(
  snapshot: TreeSnapshot,
  pulseId: number,
  pulseUpdate: Partial<PulseTreeData> & {
    previewShareActive?: boolean | null;
    finalShareActive?: boolean | null;
    linkedSharePulseId?: number | null;
    linkedShareType?: 'preview' | 'final' | null;
    linkedShareActive?: boolean | null;
  }
): TreeSnapshot {
  // Subset of the update that also applies to `pulseTimings` entries.
  const timingUpdate: Record<string, unknown> = {};
  for (const field of PULSE_TIMING_FIELDS) {
    if (field in pulseUpdate) {
      timingUpdate[field] = (pulseUpdate as Record<string, unknown>)[field];
    }
  }
  const hasTimingUpdate = Object.keys(timingUpdate).length > 0;

  return {
    ...snapshot,
    years: snapshot.years.map(year => ({
      ...year,
      projects: year.projects.map(project => {
        // Patch the timeline's timing projection independently of the rich
        // pulse rows — snapshots fetched with includePulses=false carry
        // `pulseTimings` but no `pulses`.
        let timings = project.pulseTimings;
        if (hasTimingUpdate && timings?.some(t => t.id === pulseId)) {
          timings = timings.map(t =>
            t.id === pulseId ? { ...t, ...timingUpdate } : t
          );
        }

        const pulseIndex = project.pulses?.findIndex(p => p.id === pulseId) ?? -1;
        if (pulseIndex === -1) {
          return timings !== project.pulseTimings
            ? { ...project, pulseTimings: timings }
            : project;
        }

        return {
          ...project,
          ...(timings !== project.pulseTimings ? { pulseTimings: timings } : {}),
          pulses: project.pulses!.map(pulse =>
            pulse.id === pulseId
              ? { ...pulse, ...pulseUpdate }
              : pulse
          )
        };
      })
    })),
    pulseMap: snapshot.pulseMap
      ? {
          ...snapshot.pulseMap,
          [pulseId]: snapshot.pulseMap[pulseId]
            ? { ...snapshot.pulseMap[pulseId], ...pulseUpdate }
            : pulseUpdate as PulseTreeData
        }
      : undefined
  };
}

/**
 * Add a new project to a year's projects array
 */
export function addProjectToSnapshot(
  snapshot: TreeSnapshot,
  yearId: string,
  project: ProjectTreeData
): TreeSnapshot {
  return {
    ...snapshot,
    years: snapshot.years.map(year => {
      if (year.id !== yearId) return year;

      return {
        ...year,
        projects: [...year.projects, project]
      };
    }),
    projectMap: snapshot.projectMap
      ? {
          ...snapshot.projectMap,
          [project.id]: project
        }
      : undefined,
    totalProjects: snapshot.totalProjects + 1
  };
}

/**
 * Add a new pulse to a project's pulses array
 * 
 * FIX: Added deduplication to prevent adding the same pulse twice
 * This can happen if a WebSocket event is processed multiple times
 */
export function addPulseToSnapshot(
  snapshot: TreeSnapshot,
  projectId: string,
  pulse: PulseTreeData
): TreeSnapshot {
  // Check if pulse already exists in the project (deduplication)
  let pulseAlreadyExists = false;
  
  for (const year of snapshot.years) {
    const project = year.projects.find(p => p.id === projectId);
    if (project?.pulses) {
      pulseAlreadyExists = project.pulses.some(p => p.id === pulse.id);
      if (pulseAlreadyExists) {
        console.log('[TreeCache] Pulse already exists, skipping duplicate add:', pulse.id);
        return snapshot; // Return unchanged snapshot
      }
    }
  }
  
  return {
    ...snapshot,
    years: snapshot.years.map(year => ({
      ...year,
      projects: year.projects.map(project => {
        if (project.id !== projectId) return project;

        return {
          ...project,
          pulses: project.pulses
            ? [...project.pulses, pulse]
            : [pulse],
          pulseCount: (project.pulseCount || 0) + 1
        };
      })
    })),
    pulseMap: snapshot.pulseMap
      ? {
          ...snapshot.pulseMap,
          [pulse.id]: pulse
        }
      : undefined,
    totalPulses: snapshot.totalPulses + 1
  };
}

/**
 * Remove a pulse from the snapshot
 */
export function removePulseFromSnapshot(
  snapshot: TreeSnapshot,
  pulseId: number
): TreeSnapshot {
  let pulseRemoved = false;

  const updatedSnapshot = {
    ...snapshot,
    years: snapshot.years.map(year => ({
      ...year,
      projects: year.projects.map(project => {
        if (!project.pulses) return project;

        const filteredPulses = project.pulses.filter(p => p.id !== pulseId);
        if (filteredPulses.length !== project.pulses.length) {
          pulseRemoved = true;
          return {
            ...project,
            pulses: filteredPulses,
            pulseCount: Math.max(0, (project.pulseCount || 0) - 1)
          };
        }

        return project;
      })
    })),
    pulseMap: snapshot.pulseMap
      ? Object.fromEntries(
          Object.entries(snapshot.pulseMap).filter(([key]) => key !== pulseId.toString())
        )
      : undefined,
    totalPulses: pulseRemoved ? Math.max(0, snapshot.totalPulses - 1) : snapshot.totalPulses
  };

  return updatedSnapshot;
}

/**
 * Update a task within a pulse - FIXED to work with aggregated PulseTreeData
 * 
 * NOTE: PulseTreeData only has taskCounts (aggregated), NOT a tasks array.
 * We intelligently update counts based on status changes without needing the full tasks list.
 */
export function updateTaskInPulse(
  snapshot: TreeSnapshot,
  pulseId: number,
  taskId: number,
  taskUpdate: Partial<TaskDetail>,
  oldTaskStatus?: string
): TreeSnapshot {
  console.log('[TreeCache] updateTaskInPulse:', { pulseId, taskId, taskUpdate, oldTaskStatus });

  const pulse = findPulseInSnapshot(snapshot, pulseId);
  if (!pulse) {
    console.warn('[TreeCache] Pulse not found in snapshot:', pulseId);
    return snapshot;
  }

  // If this is a status change, calculate the count delta
  let taskCountsUpdate: Partial<TaskCounts> | undefined;
  
  if (taskUpdate.status && oldTaskStatus && taskUpdate.status !== oldTaskStatus) {
    const oldCounts = pulse.taskCounts || { all: 0, done: 0, working: 0, other: 0 };
    const newCounts = { ...oldCounts };

    // Decrement old status count
    const oldField = getCountFieldForStatus(oldTaskStatus);
    newCounts[oldField] = Math.max(0, newCounts[oldField] - 1);

    // Increment new status count
    const newField = getCountFieldForStatus(taskUpdate.status);
    newCounts[newField] = newCounts[newField] + 1;

    taskCountsUpdate = newCounts;
    console.log('[TreeCache] Calculated taskCounts delta:', {
      old: oldCounts,
      new: newCounts,
      oldStatus: oldTaskStatus,
      newStatus: taskUpdate.status
    });
  }

  // Update the pulse with new task counts
  if (taskCountsUpdate) {
    return updatePulseInSnapshot(snapshot, pulseId, {
      taskCounts: taskCountsUpdate as TaskCounts
    });
  }

  // If no status change, return unchanged (other task field updates don't affect counts)
  return snapshot;
}

/**
 * Add a task to a pulse - update counts accordingly
 */
export function addTaskToPulse(
  snapshot: TreeSnapshot,
  pulseId: number,
  task: TaskDetail
): TreeSnapshot {
  console.log('[TreeCache] addTaskToPulse:', { pulseId, taskId: task.id, status: task.status });

  const pulse = findPulseInSnapshot(snapshot, pulseId);
  if (!pulse) {
    console.warn('[TreeCache] Pulse not found in snapshot:', pulseId);
    return snapshot;
  }

  const oldCounts = pulse.taskCounts || { all: 0, done: 0, working: 0, other: 0 };
  const newCounts = { ...oldCounts };

  // Increment total count
  newCounts.all = newCounts.all + 1;

  // Increment appropriate status count
  const statusField = getCountFieldForStatus(task.status);
  newCounts[statusField] = newCounts[statusField] + 1;

  console.log('[TreeCache] Task created, updated counts:', {
    old: oldCounts,
    new: newCounts,
    taskStatus: task.status
  });

  return updatePulseInSnapshot(snapshot, pulseId, {
    taskCounts: newCounts,
    hasTasks: true
  });
}

/**
 * Remove a task from a pulse - update counts accordingly
 */
export function removeTaskFromPulse(
  snapshot: TreeSnapshot,
  pulseId: number,
  task: TaskDetail
): TreeSnapshot {
  console.log('[TreeCache] removeTaskFromPulse:', { pulseId, taskId: task.id, status: task.status });

  const pulse = findPulseInSnapshot(snapshot, pulseId);
  if (!pulse) {
    console.warn('[TreeCache] Pulse not found in snapshot:', pulseId);
    return snapshot;
  }

  const oldCounts = pulse.taskCounts || { all: 0, done: 0, working: 0, other: 0 };
  const newCounts = { ...oldCounts };

  // Decrement total count
  newCounts.all = Math.max(0, newCounts.all - 1);

  // Decrement appropriate status count
  const statusField = getCountFieldForStatus(task.status);
  newCounts[statusField] = Math.max(0, newCounts[statusField] - 1);

  console.log('[TreeCache] Task deleted, updated counts:', {
    old: oldCounts,
    new: newCounts,
    taskStatus: task.status
  });

  return updatePulseInSnapshot(snapshot, pulseId, {
    taskCounts: newCounts,
    hasTasks: newCounts.all > 0
  });
}

/**
 * Calculate task counts from tasks array (for PulseDetail with tasks)
 */
export function recalculatePulseTaskCounts(tasks: TaskDetail[]): TaskCounts {
  const all = tasks.length;
  const done = tasks.filter(t => t.status === 'done').length;
  const working = tasks.filter(t => t.status === 'working_on_it').length;
  const other = tasks.filter(t => t.status !== 'done' && t.status !== 'working_on_it').length;

  return {
    all,
    done,
    working,
    other
  };
}

/**
 * Main function to apply WebSocket events to the snapshot
 * 
 * FIXED: Now correctly handles aggregated PulseTreeData structure
 */
export function applyWebSocketEvent(
  snapshot: TreeSnapshot | undefined,
  event: WebSocketEvent
): TreeSnapshot | undefined {
  if (!snapshot) return undefined;

  const { type, data } = event;

  // Defensive checks - validate event data structure
  if (!data) {
    console.warn('[TreeCache] Invalid event - missing data:', event);
    return snapshot;
  }

  try {
    switch (type) {
      // Handle new delta format: entity.create with entity='pulse'
      case 'entity.create': {
        if ((event as any).entity === 'pulse' && (event as any).payload) {
          const pulse = (event as any).payload;
          const projectId = (event as any).meta?.projectId || pulse.projectId;
          if (projectId && pulse) {
            console.log('[TreeCache] Handling entity.create for pulse:', { pulseId: pulse.id, projectId });
            return addPulseToSnapshot(snapshot, projectId, pulse);
          }
        }
        console.warn('[TreeCache] Unhandled entity.create event:', event);
        return snapshot;
      }

      // Handle new delta format: entity.update with entity='pulse'
      case 'entity.update': {
        if ((event as any).entity === 'pulse') {
          const payload = (event as any).payload;
          const pulseId = payload?.pulseId || (event as any).meta?.pulseId || (event as any).id;
          const fields = (event as any).fields as string[] | undefined;
          
          if (pulseId) {
            // CRITICAL: For sequence updates, only merge the sequences field
            // This preserves existing taskCounts, timeTracking, artist, hasTasks
            if (fields?.includes('sequences') && payload?.sequences) {
              console.log('[TreeCache] Handling entity.update for pulse sequences:', { pulseId, sequenceCount: payload.sequences.length });
              return updatePulseInSnapshot(snapshot, pulseId, {
                sequences: payload.sequences
              });
            }
            
            // For other field-specific updates, only merge those specific fields
            // Do NOT apply payload wholesale to prevent overwriting existing data
            if (fields && fields.length > 0 && payload) {
              const partialUpdate: Partial<PulseTreeData> = {};
              for (const field of fields) {
                if (field in payload && field !== 'pulseId') {
                  (partialUpdate as any)[field] = payload[field];
                }
              }
              if (Object.keys(partialUpdate).length > 0) {
                console.log('[TreeCache] Handling entity.update for pulse with fields:', { pulseId, fields, updateKeys: Object.keys(partialUpdate) });
                return updatePulseInSnapshot(snapshot, pulseId, partialUpdate);
              }
            }
            
            // Handle general pulse updates without fields metadata
            // Only apply if this is a full pulse update (has core pulse properties)
            if (payload && !fields) {
              const isPulseUpdate = 'status' in payload || 'pulseDescription' in payload || 'dueDate' in payload;
              if (isPulseUpdate) {
                console.log('[TreeCache] Handling entity.update for pulse (full update):', { pulseId });
                return updatePulseInSnapshot(snapshot, pulseId, payload);
              }
            }
          }
        }
        console.warn('[TreeCache] Unhandled entity.update event:', event);
        return snapshot;
      }

      case 'pulse.created':
      case 'pulse_created': {
        // Handle both old format (data.pulse) and new format (data itself is the pulse)
        const pulse = data.pulse || data.data || data;
        const projectId = data.projectId || (event as any).meta?.projectId || pulse?.projectId;
        
        if (projectId && pulse && pulse.id) {
          console.log('[TreeCache] Adding pulse to snapshot:', { pulseId: pulse.id, projectId });
          return addPulseToSnapshot(snapshot, projectId, pulse);
        }
        console.warn('[TreeCache] Invalid pulse.created event - missing pulse or projectId:', {
          hasPulse: !!pulse,
          hasProjectId: !!projectId,
          event
        });
        return snapshot;
      }

      case 'pulse.updated':
        if (data.pulseId && data.update) {
          return updatePulseInSnapshot(snapshot, data.pulseId, data.update);
        }
        console.warn('[TreeCache] Invalid pulse.updated event:', data);
        return snapshot;

      case 'pulse.deleted':
        if (data.pulseId) {
          return removePulseFromSnapshot(snapshot, data.pulseId);
        }
        console.warn('[TreeCache] Invalid pulse.deleted event:', data);
        return snapshot;

      case 'task.created':
        // WebSocket sends: { type: 'task_created', taskId, pulseId, data: TaskDetail }
        if (data.pulseId && data.data) {
          return addTaskToPulse(snapshot, data.pulseId, data.data);
        }
        console.warn('[TreeCache] Invalid task.created event:', data);
        return snapshot;

      case 'task.updated':
        // WebSocket sends: { type: 'task_updated', taskId, pulseId, fields, data: TaskDetail, oldStatus? }
        if (data.pulseId && data.taskId && data.data) {
          // If status field changed, we need the old status to calculate delta
          const isStatusChange = data.fields?.includes('status');
          
          if (isStatusChange && data.oldStatus && data.data.status) {
            // Only update if we have different statuses
            if (data.data.status !== data.oldStatus) {
              console.log('[TreeCache] Task status changed:', {
                taskId: data.taskId,
                pulseId: data.pulseId,
                oldStatus: data.oldStatus,
                newStatus: data.data.status
              });
              
              return updateTaskInPulse(
                snapshot, 
                data.pulseId, 
                data.taskId, 
                data.data,
                data.oldStatus
              );
            } else {
              console.log('[TreeCache] Status unchanged, skipping count update');
            }
          } else if (isStatusChange && !data.oldStatus) {
            console.warn('[TreeCache] Status change detected but oldStatus not available, relying on invalidation');
          }
          
          // For non-status updates or when oldStatus is unavailable, no count changes needed
          // The invalidation will ensure the UI refetches with correct data
          return snapshot;
        }
        console.warn('[TreeCache] Invalid task.updated event:', data);
        return snapshot;

      case 'task.deleted':
        // WebSocket sends: { type: 'task_deleted', taskId, pulseId, data: TaskDetail }
        if (data.pulseId && data.data) {
          return removeTaskFromPulse(snapshot, data.pulseId, data.data);
        }
        console.warn('[TreeCache] Invalid task.deleted event:', data);
        return snapshot;

      case 'progress.updated':
        // Update pulse with new progress/status
        if (data.pulseId && data.progress) {
          return updatePulseInSnapshot(snapshot, data.pulseId, {
            status: data.progress.status || data.progress
          });
        }
        console.warn('[TreeCache] Invalid progress.updated event:', data);
        return snapshot;

      case 'sequence.updated':
        // Update pulse sequences
        if (data.pulseId && data.sequences) {
          return updatePulseInSnapshot(snapshot, data.pulseId, {
            sequences: data.sequences
          });
        }
        console.warn('[TreeCache] Invalid sequence.updated event:', data);
        return snapshot;

      case 'project.created':
        if (data.yearId && data.id) {
          // Create a minimal project object from the delta data
          const newProject: ProjectTreeData = {
            id: data.id,
            yearId: data.yearId,
            clientId: data.clientId || '',
            projectName: data.projectName || '',
            leadArtist: data.leadArtist || '',
            projectStatus: data.projectStatus || 'in Production',
            taskStatus: data.taskStatus || 'active',
            dateAdded: data.dateAdded || new Date().toISOString(),
            dueDate: data.dueDate || null,
            deadline: data.deadline || null,
            timeTrackingEnabled: data.timeTrackingEnabled || false,
            thumbnail: data.thumbnail || null,
            pulseCount: 0,
            taskCount: 0,
            client: data.client || { id: data.clientId || '', companyName: '' },
            leadArtistInfo: data.leadArtistInfo || null,
            sequences: data.sequences || [],
            artists: data.artists || [],
            pulses: []
          };
          return addProjectToSnapshot(snapshot, data.yearId, newProject);
        }
        console.warn('[TreeCache] Invalid project.created event:', data);
        return snapshot;

      case 'project.updated': {
        const projId = data.projectId;
        if (!projId) {
          console.warn('[TreeCache] Invalid project.updated event - no projectId:', data);
          return snapshot;
        }
        if (data.update) {
          return updateProjectInSnapshot(snapshot, { id: projId, ...data.update });
        }
        const { projectName, projectStatus, taskStatus, leadArtist,
          dueDate, deadline, thumbnail, lifecycleState,
          communicationMode, timeTrackingEnabled, clientId, dateAdded } = data;
        const projectMerge = {
          id: projId,
          ...(projectName !== undefined && { projectName }),
          ...(projectStatus !== undefined && { projectStatus }),
          ...(taskStatus !== undefined && { taskStatus }),
          ...(leadArtist !== undefined && { leadArtist }),
          ...(dueDate !== undefined && { dueDate }),
          ...(deadline !== undefined && { deadline }),
          ...(thumbnail !== undefined && { thumbnail }),
          ...(lifecycleState !== undefined && { lifecycleState }),
          ...(communicationMode !== undefined && { communicationMode }),
          ...(timeTrackingEnabled !== undefined && { timeTrackingEnabled }),
          ...(clientId !== undefined && { clientId }),
          ...(dateAdded !== undefined && { dateAdded }),
        };
        if (Object.keys(projectMerge).length > 1) {
          console.log('[TreeCache] Handling project.updated via direct merge:', { projectId: projId, mergedFields: Object.keys(projectMerge).filter(k => k !== 'id') });
          return updateProjectInSnapshot(snapshot, projectMerge as Partial<ProjectTreeData>);
        }
        console.warn('[TreeCache] project.updated event has no applicable fields:', data);
        return snapshot;
      }

      default:
        // Unknown event type - return snapshot unchanged
        // Note: chat_message events are expected here but handled via invalidation, not delta
        if (type !== 'chat_message' && !type?.startsWith('chat_')) {
          console.log('[TreeCache] Unknown event type:', type);
        }
        return snapshot;
    }
  } catch (error) {
    console.error('[TreeCache] Error applying WebSocket event:', error, event);
    return snapshot;
  }
}

/**
 * Helper function to find a pulse in the snapshot
 */
function findPulseInSnapshot(
  snapshot: TreeSnapshot,
  pulseId: number
): PulseTreeData | undefined {
  // Check pulseMap first if available
  if (snapshot.pulseMap && snapshot.pulseMap[pulseId]) {
    return snapshot.pulseMap[pulseId];
  }

  // Search through years and projects
  for (const year of snapshot.years) {
    for (const project of year.projects) {
      if (project.pulses) {
        const pulse = project.pulses.find(p => p.id === pulseId);
        if (pulse) return pulse;
      }
    }
  }

  return undefined;
}
